<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/Page-header";
import CheckModal from "@/components/CheckModal";
import DocumentActions from "@/components/document/DocumentActions.vue";
import workPlanAPI from "@/services/standards/workPlan/workPlanAPI";
import newScoresAPI from "@/services/newScores/newScoresAPI";
import Swal from "sweetalert2";
import VersionNote from "@/components/document/VersionNote.vue";

const MONTHS = [
  { n: 1, label: "Ene" }, { n: 2, label: "Feb" }, { n: 3, label: "Mar" }, { n: 4, label: "Abr" },
  { n: 5, label: "May" }, { n: 6, label: "Jun" }, { n: 7, label: "Jul" }, { n: 8, label: "Ago" },
  { n: 9, label: "Sep" }, { n: 10, label: "Oct" }, { n: 11, label: "Nov" }, { n: 12, label: "Dic" },
];

function blankActivity() {
  return { objetivo: "", actividad: "", responsable: "", planned_months: [], executed_months: [] };
}

export default {
  page: { title: "Plan de Trabajo Anual SG-SST" },
  components: { VersionNote, Layout, PageHeader, CheckModal, DocumentActions },
  data() {
    return {
      // Texto que queda en el control de versiones del PDF
      versionNote: "",
      title: "Plan de Trabajo Anual SG-SST",
      items: [
        { text: "Estándares", href: "/forms/estandares" },
        { text: "2.4.1 Plan de Trabajo Anual", active: true },
      ],
      companyID: this.$store.getters["company/getCompany"].id || null,
      planId: this.$route.params.plan || null,
      monthOptions: MONTHS.map((m) => ({ value: m.n, text: m.label })),
      form: {
        year: new Date().getFullYear(),
        objetivo_general: null,
      },
      activities: [blankActivity()],
      workflowStatus: "draft",
      signedAt: null,
      submitting: false,
      loading: true,
    };
  },
  computed: {
    isSigned() {
      return this.workflowStatus === "signed";
    },
    workPlanApi() {
      return {
        pdfUrl: (id) => workPlanAPI.pdfUrl(id),
        sign: (id, payload) => workPlanAPI.sign(id, payload),
      };
    },
    totalP() {
      return this.activities.reduce((s, a) => s + (a.planned_months || []).length, 0);
    },
    totalE() {
      return this.activities.reduce((s, a) => s + (a.executed_months || []).length, 0);
    },
    pctAnual() {
      return this.totalP > 0 ? Math.round((this.totalE / this.totalP) * 100) : 0;
    },
  },
  methods: {
    goBack() {
      this.$router.go(-1);
    },
    actPct(a) {
      const p = (a.planned_months || []).length;
      const e = (a.executed_months || []).length;
      return p > 0 ? Math.round((e / p) * 100) : 0;
    },
    addActivity() {
      if (this.isSigned) return;
      this.activities.push(blankActivity());
    },
    removeActivity(idx) {
      if (this.isSigned) return;
      this.activities.splice(idx, 1);
      if (!this.activities.length) this.activities.push(blankActivity());
    },
    load() {
      if (!this.planId) { this.loading = false; return; }
      workPlanAPI.get(this.planId)
        .then((r) => {
          if (!r) { this.loading = false; return; }
          this.form.year = r.year || this.form.year;
          this.form.objetivo_general = r.objetivo_general || null;
          const acts = Array.isArray(r.activities) ? r.activities : [];
          this.activities = acts.length
            ? acts.map((a) => ({
                objetivo: a.objetivo || "",
                actividad: a.actividad || "",
                responsable: a.responsable || "",
                planned_months: Array.isArray(a.planned_months) ? a.planned_months.map(Number) : [],
                executed_months: Array.isArray(a.executed_months) ? a.executed_months.map(Number) : [],
              }))
            : [blankActivity()];
          this.workflowStatus = r.workflow_status || "draft";
          this.signedAt = r.signed_at || null;
          this.loading = false;
        })
        .catch((e) => { console.log(e); this.loading = false; });
    },
    save() {
      if (this.isSigned) return;
      this.submitting = true;
      const payload = {
        companies_id: this.companyID,
        year: this.form.year,
        objetivo_general: this.form.objetivo_general,
        activities: this.activities,
      };
      payload.version_note = this.versionNote;
      const req = this.planId
        ? workPlanAPI.update(this.planId, payload)
        : workPlanAPI.save(payload);
      req
        .then(async (res) => {
          // http() no rechaza en errores HTTP: devuelve el body igual. El backend
          // responde 409 con `existing_id` cuando ya hay un plan para ese año, asi
          // que hay que detectarlo aca o se mostraria "guardado con exito".
          if (res && res.existing_id) {
            this.submitting = false;
            const r = await Swal.fire({
              title: `Ya existe un plan para ${this.form.year}`,
              text: res.message,
              icon: "warning",
              showCancelButton: !res.signed,
              confirmButtonText: res.signed ? "Entendido" : "Abrir el plan existente",
              cancelButtonText: "Cancelar",
            });
            if (!res.signed && (r.value || r.isConfirmed)) {
              this.$router.push({
                path: `/forms/estandares/${this.companyID}/plan-trabajo/${res.existing_id}`,
              });
            }
            return;
          }

          newScoresAPI.calculateScores({ companies_id: this.companyID, item_id: 15, status: "Pendiente" }).catch(() => {});
          Swal.fire({ title: "¡Éxito!", text: "Plan guardado con éxito.", icon: "success", timer: 1800, showConfirmButton: false });
          this.submitting = false;
          if (!this.planId && res && res.id) {
            this.planId = res.id;
            this.$router.replace({ path: `/forms/estandares/${this.companyID}/plan-trabajo/${res.id}` });
          }
        })
        .catch((e) => {
          console.log(e);
          this.submitting = false;
          Swal.fire("Error", "No se pudo guardar el plan", "error");
        });
    },
    onSigned(payload) {
      this.workflowStatus = payload.workflow_status;
      this.signedAt = payload.signed_at;
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
      <div class="col-lg-9" v-if="!loading">
        <div class="card">
          <div class="card-body">
            <CheckModal>
              <p class="text-justify">
                Diligencia el Plan de Trabajo Anual del SG-SST: define el objetivo general, las actividades por
                objetivo/criterio con su responsable, y marca en qué meses cada actividad está
                <strong>Planificada (P)</strong> y en cuáles fue <strong>Ejecutada (E)</strong>. La plataforma genera el
                plan branded para la firma del Representante Legal y calcula el porcentaje de avance.
              </p>
            </CheckModal>

            <div v-if="isSigned" class="alert alert-success py-2 my-2 small">
              🔒 Este plan ya fue firmado; los datos quedan bloqueados para edición.
            </div>

            <form @submit.prevent="save">
              <div class="form-row">
                <b-form-group class="col-md-3 px-2" label="Año del plan">
                  <b-form-input type="number" min="2000" max="2100" v-model.number="form.year" :disabled="isSigned" required />
                </b-form-group>
                <b-form-group class="col-md-9 px-2" label="Objetivo general del SG-SST">
                  <b-form-textarea v-model="form.objetivo_general" :disabled="isSigned" rows="2"
                    placeholder="Objetivo general del sistema para la vigencia..." />
                </b-form-group>
              </div>

              <hr />
              <div class="d-flex justify-content-between align-items-center">
                <h5 class="mb-0">Actividades del plan</h5>
                <span class="badge badge-info">% Avance anual: {{ pctAnual }}% · P {{ totalP }} / E {{ totalE }}</span>
              </div>
              <p class="text-muted small mb-2">Marca los meses Planificados (P) y Ejecutados (E) de cada actividad.</p>

              <div v-for="(a, idx) in activities" :key="idx" class="border rounded p-2 mb-3">
                <div class="form-row">
                  <b-form-group class="col-md-1 px-2" label="N°">
                    <div class="pt-2 font-weight-bold">{{ idx + 1 }}</div>
                  </b-form-group>
                  <b-form-group class="col-md-4 px-2" label="Objetivo / Criterio">
                    <b-form-input type="text" v-model="a.objetivo" :disabled="isSigned" />
                  </b-form-group>
                  <b-form-group class="col-md-4 px-2" label="Actividad específica">
                    <b-form-input type="text" v-model="a.actividad" :disabled="isSigned" />
                  </b-form-group>
                  <b-form-group class="col-md-3 px-2" label="Responsable">
                    <b-form-input type="text" v-model="a.responsable" :disabled="isSigned" />
                  </b-form-group>
                </div>

                <div class="form-row align-items-center">
                  <label class="col-md-2 px-2 mb-1 small font-weight-bold text-primary">Planificado (P)</label>
                  <div class="col-md-10 px-2 mb-1">
                    <b-form-checkbox-group v-model="a.planned_months" :options="monthOptions" :disabled="isSigned"
                      buttons button-variant="outline-primary" size="sm" />
                  </div>
                  <label class="col-md-2 px-2 mb-1 small font-weight-bold text-success">Ejecutado (E)</label>
                  <div class="col-md-10 px-2 mb-1">
                    <b-form-checkbox-group v-model="a.executed_months" :options="monthOptions" :disabled="isSigned"
                      buttons button-variant="outline-success" size="sm" />
                  </div>
                </div>

                <div class="text-right">
                  <span class="small text-muted mr-2">% avance actividad: {{ actPct(a) }}%</span>
                  <button type="button" class="btn btn-outline-danger btn-sm" :disabled="isSigned"
                    @click="removeActivity(idx)">
                    <i class="mdi mdi-trash-can"></i> Quitar
                  </button>
                </div>
              </div>

              <button type="button" class="btn btn-outline-primary btn-sm mb-3" :disabled="isSigned" @click="addActivity">
                <i class="mdi mdi-plus"></i> Agregar actividad
              </button>

              <VersionNote v-model="versionNote" :editing="!!planId" />

              <div class="text-right">
                <button type="submit" :disabled="isSigned || submitting" class="btn btn-primary mx-1">Guardar</button>
                <button type="button" class="btn btn-secondary mx-1" @click="goBack">Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <div class="col-lg-3" v-if="!loading && planId">
        <h6 class="text-muted small mb-1">Plan de Trabajo Anual</h6>
        <DocumentActions
          :document-id="Number(planId)"
          :company-id="companyID"
          :workflow-status="workflowStatus"
          :signed-at="signedAt"
          :api-service="workPlanApi"
          document-title="Plan de Trabajo Anual SG-SST"
          signer-role="gerente"
          @signed="onSigned"
        />
      </div>

      <div v-if="loading" class="col-12 text-center">
        <b-spinner variant="primary" label="Loading..."></b-spinner>
      </div>
    </div>
  </Layout>
</template>
